import { useCallback, useState } from "react";
import type { ContactDto, CustomerDetailDto } from "@grizzly/shared";
import { api, ApiClientError } from "../../lib/apiClient.js";
import { useI18n } from "../../i18n/index.js";
import { Button } from "../ui/button.js";
import { Card } from "../ui/card.js";
import { TextInput } from "../ui/field.js";
import { useToast } from "../../lib/useToast.js";
import { Phone, Plus, Trash2, UserRound } from "lucide-react";

interface Form {
  name: string;
  title: string;
  phone: string;
  email: string;
  isPrimary: boolean;
}

const EMPTY_FORM: Form = { name: "", title: "", phone: "", email: "", isPrimary: false };

export function ContactSection({
  customer,
  onChanged,
}: {
  customer: CustomerDetailDto;
  onChanged(): void;
}) {
  const { push } = useToast();
  const { t } = useI18n();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState<Form>(EMPTY_FORM);
  const [busy, setBusy] = useState(false);

  const submit = useCallback(async () => {
    setBusy(true);
    try {
      await api.addContact(customer.id, form);
      push(t("customers.contacts.addedToast"), form.name);
      setForm(EMPTY_FORM);
      setAdding(false);
      onChanged();
    } catch (err) {
      if (err instanceof ApiClientError) push(err.message, undefined, "danger");
    } finally {
      setBusy(false);
    }
  }, [customer.id, form, onChanged, push, t]);

  const remove = useCallback(
    async (contact: ContactDto) => {
      try {
        await api.deleteContact(customer.id, contact.id);
        push(t("customers.contacts.removedToast"), contact.name);
        onChanged();
      } catch (err) {
        if (err instanceof ApiClientError) push(err.message, undefined, "danger");
      }
    },
    [customer.id, onChanged, push, t]
  );

  return (
    <Card>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-ink">
          {t("customers.contacts.title")}
        </h2>
        <Button size="sm" variant="secondary" onClick={() => setAdding((v) => !v)}>
          <Plus className="h-4 w-4" aria-hidden="true" />
          {t("customers.contacts.add")}
        </Button>
      </div>

      {customer.contacts.length === 0 && !adding ? (
        <p className="text-sm text-ink-muted">{t("customers.contacts.empty")}</p>
      ) : (
        <ul className="space-y-2">
          {customer.contacts.map((contact) => (
            <li
              key={contact.id}
              className="flex items-center justify-between gap-2 rounded border border-line px-3 py-2"
            >
              <div className="min-w-0">
                <p className="flex items-center gap-2 text-sm font-medium text-ink">
                  <UserRound
                    className="h-4 w-4 shrink-0 text-ink-muted"
                    aria-hidden="true"
                  />
                  {contact.name}
                  {contact.isPrimary ? (
                    <BadgeSmall>{t("common.primary")}</BadgeSmall>
                  ) : null}
                </p>
                {(contact.phone || contact.email) && (
                  <p className="mt-0.5 flex items-center gap-1 text-xs text-ink-muted">
                    <Phone className="h-3 w-3" aria-hidden="true" />
                    {[contact.phone, contact.email].filter(Boolean).join(" · ")}
                  </p>
                )}
              </div>
              <Button size="sm" variant="ghost" onClick={() => void remove(contact)}>
                <Trash2 className="h-4 w-4" aria-hidden="true" />
                {t("common.remove")}
              </Button>
            </li>
          ))}
        </ul>
      )}

      {adding ? (
        <div className="mt-3 space-y-3 border-t border-line pt-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <TextInput
              label={t("common.fields.name")}
              required
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            />
            <TextInput
              label={t("common.fields.title")}
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <TextInput
              label={t("common.fields.phone")}
              type="tel"
              value={form.phone}
              onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
            />
            <TextInput
              label={t("common.fields.email")}
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-ink">
            <input
              type="checkbox"
              checked={form.isPrimary}
              onChange={(e) => setForm((f) => ({ ...f, isPrimary: e.target.checked }))}
            />
            {t("common.primaryContact")}
          </label>
          <div className="flex gap-2">
            <Button
              variant="primary"
              onClick={submit}
              disabled={busy || !form.name.trim()}
            >
              {busy ? t("common.saving") : t("customers.contacts.saved")}
            </Button>
            <Button variant="ghost" onClick={() => setAdding(false)}>
              {t("common.cancel")}
            </Button>
          </div>
        </div>
      ) : null}
    </Card>
  );
}

function BadgeSmall({ children }: { children: React.ReactNode }) {
  return (
    <span className="rounded-full bg-status-ready/10 px-2 py-0.5 text-[0.65rem] font-medium text-status-ready">
      {children}
    </span>
  );
}
